import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import VolumetricBackground from '../components/app/VolumetricBackground';
import LeftSidebar from '../components/sidebars';
import TopNavigation from '../components/navigation/app';
import RightSidebar from '../components/sidebars/RightSidebar';
import ContlyRightSidebar from '../components/sidebars/ContlyRightSidebar';
import ChatView from '../components/app/ChatView';
import TaxperView from '../components/app/TaxperView';
import ContlyView from '../components/app/ContlyView';

import { getTheme, toggleTheme } from '../utils/theme';
import { useViewRouter } from '../hooks/useViewRouter';
import { useSidebar } from '../hooks/useSidebar';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { useDocuments } from '../contexts/DocumentContext';

/**
 * AppMain Page
 * 
 * Main application page with three views: Chat, Taxper, and Contly.
 * Features three-panel layout with left sidebar, main content, and right sidebar.
 * 
 * Fully implemented according to CHAT_TAXPER_CONTLY_PAGES_DESIGN_SPEC.md
 * 
 * Features:
 * - Left sidebar (collapsible, chat history/documents)
 * - Top navigation bar with view tabs
 * - Main content area (Chat, Taxper, or Contly view based on hash routing)
 * - Dark/Light theme toggle
 * - URL hash routing (#chat, #taxper, #contly)
 * - Chat view with premium morphing input bar
 * - Taxper form with category tags
 * - Contly document management
 * - Volumetric light beam background
 */
const AppMain = () => {
  const navigate = useNavigate();
  const { view, isViewTransitioning, viewOpacity, handleViewChange } = useViewRouter();
  const { sidebarCollapsed, toggleSidebar } = useSidebar();
  const { switchApp, sessions, createNewSession, selectSession, currentSessionId, deleteSession } = useChat(); // Use ChatContext including session props
  const { userData, currentUser } = useAuth();
  const { documents, deleteDocument } = useDocuments();
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage on mount
    return getTheme();
  });

  // Sync view with ChatContext
  useEffect(() => {
    switchApp(view);
  }, [view, switchApp]);
  const [openedDocumentId, setOpenedDocumentId] = useState(null);
  const openedDocument = documents.find(d => d.id === openedDocumentId) || null;
  const [contlyFilter, setContlyFilter] = useState('all');
  const [contlySearchQuery, setContlySearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [triggerUpload, setTriggerUpload] = useState(false);
  const [taxperFilter, setTaxperFilter] = useState('all');
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect desktop breakpoint (1024px = lg)
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // CRITICAL: Prevent body scroll on mobile when in app views
  useEffect(() => {
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      document.body.classList.add('app-main-active');
      document.documentElement.classList.add('app-main-active');
    } else {
      document.body.classList.remove('app-main-active');
      document.documentElement.classList.remove('app-main-active');
    }

    return () => {
      document.body.classList.remove('app-main-active');
      document.documentElement.classList.remove('app-main-active');
    };
  }, [isDesktop]);

  const handleThemeToggle = () => {
    setTheme(prev => {
      const newTheme = toggleTheme(prev);
      // Theme is now scoped to AppMain container only, not applied globally
      return newTheme;
    });
  };

  const handleNewChat = () => {
    // Create new session via context
    createNewSession();
    // Ensure we are in the correct view (though new session is specific to active app anyway)
    // handleViewChange('chat'); // User might want to stay in current app context, createNewSession uses activeApp
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleDocumentClick = (document) => {
    if (document && view === 'contly') {
      if (openedDocumentId === document.id) {
        setOpenedDocumentId(null);
      } else {
        setOpenedDocumentId(document.id);
      }
    }
  };

  const handleDocumentOpened = (doc) => {
    // We keep the ID set so the document remains "active" in the editor
    console.log('Document active in Contly:', doc?.name || doc?.id);
  };

  return (
    <div
      className={`app-main min-h-screen w-full overflow-x-hidden ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'} relative`}
      data-theme={theme}
      style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}
    >
      {/* Volumetric Light Beam Background - EXACT: 3 gradients, blur(80px), opacity 0.6 */}
      <VolumetricBackground theme={theme} />

      {/* Left Sidebar */}
      <LeftSidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onNewChat={handleNewChat}
        onProfileClick={handleProfileClick}
        theme={theme}
        sessions={sessions}
        currentSessionId={currentSessionId}
        onSelectSession={selectSession}
        onDeleteSession={deleteSession}
        userName={userData?.fullName || currentUser?.displayName || 'User'}
      />

      {/* Main Content Area - MOBILE: No margins, accounts for top/bottom nav + safe areas */}
      <div
        className="main-content-wrapper"
        style={{
          marginLeft: isDesktop ? (sidebarCollapsed ? '80px' : '260px') : '0',
          marginRight: isDesktop && rightSidebarOpen && (view === 'taxper' || view === 'contly') ? '340px' : '0',
          paddingTop: !isDesktop ? 'calc(56px + env(safe-area-inset-top, 0px))' : '0',
          paddingBottom: !isDesktop ? 'calc(64px + env(safe-area-inset-bottom, 0px))' : '0',
          transition: 'margin-left 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), margin-right 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          width: 'auto',
          maxWidth: '100%',
          overflowX: 'hidden',
          // CRITICAL: Prevent scrolling on mobile that affects fixed children
          ...(!isDesktop && {
            overflowY: 'hidden', // Prevent body scroll
            height: '100vh',
            position: 'relative',
          }),
        }}
      >
        {/* Top Navigation */}
        <TopNavigation
          activeView={view}
          onViewChange={handleViewChange}
          theme={theme}
          onThemeToggle={handleThemeToggle}
          onRightSidebarToggle={() => setRightSidebarOpen(!rightSidebarOpen)}
          onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        />

        {/* View Container - Views handle their own right margin */}
        <div className="relative overflow-x-hidden" style={{ width: '100%', maxWidth: '100%' }}>
          <div
            className={`view-transition-container ${isViewTransitioning || viewOpacity < 1 ? 'transitioning' : 'stable'}`}
            data-fully-visible={viewOpacity === 1 && !isViewTransitioning ? 'true' : 'false'}
            data-transitioning={isViewTransitioning || viewOpacity < 1 ? 'true' : 'false'}
            style={{
              opacity: viewOpacity,
              // CRITICAL: Don't use transform on mobile - it creates containing block for fixed children
              ...(isDesktop ? {
                transform: `translateY(${viewOpacity === 1 ? 0 : 20}px)`,
                transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              } : {
                transform: 'none',
                transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }),
              willChange: isViewTransitioning && isDesktop ? 'opacity, transform' : 'opacity',
              width: '100%',
              maxWidth: '100%',
              overflowX: 'hidden',
              overflowY: 'visible',
            }}
          >
            {view === 'chat' && (
              <ChatView
                rightSidebarOpen={rightSidebarOpen}
                onCloseSidebar={() => setRightSidebarOpen(false)}
              />
            )}
            {view === 'taxper' && (
              <TaxperView
                rightSidebarOpen={rightSidebarOpen}
                onCloseIntelligence={() => setRightSidebarOpen(false)}
                triggerUpload={triggerUpload}
                onUploadTriggered={() => setTriggerUpload(false)}
                activeFilter={taxperFilter}
                onFilterChange={setTaxperFilter}
              />
            )}
            {view === 'contly' && (
              <ContlyView
                openedDocument={openedDocument}
                onDocumentOpened={handleDocumentOpened}
                onDocumentClick={handleDocumentClick}
                onClose={() => setOpenedDocumentId(null)}
                rightSidebarOpen={rightSidebarOpen}
                onCloseDocuments={() => setRightSidebarOpen(false)}
                triggerUpload={triggerUpload}
                onUploadTriggered={() => setTriggerUpload(false)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Right Sidebar - Show only on desktop (mobile uses bottom sheets) */}
      {view === 'contly' && isDesktop && (
        <ContlyRightSidebar
          hidden={!rightSidebarOpen}
          documents={documents.filter(d => d.app === 'contly')}
          activeFilter={contlyFilter}
          onFilterChange={setContlyFilter}
          onDocumentClick={handleDocumentClick}
          onAddDocument={() => setTriggerUpload(true)}
          searchQuery={contlySearchQuery}
          onSearchChange={setContlySearchQuery}
          onDeleteDocument={deleteDocument}
          theme={theme}
        />
      )}
      {view === 'taxper' && isDesktop && (
        <RightSidebar
          activeView={view}
          hidden={!rightSidebarOpen}
          documents={documents}
          onDocumentClick={handleDocumentClick}
          onAddDocument={() => setTriggerUpload(true)}
          theme={theme}
          activeFilter={taxperFilter}
          onFilterChange={setTaxperFilter}
          onDeleteDocument={deleteDocument}
        />
      )}
    </div>
  );
};

export default AppMain;

